/**
 * Reusable ChangeHistory component — collapsible audit trail for DB-backed entries.
 *
 * Used across: asset list, employment, security interests, general liabilities,
 * indicator findings, creditors, corporate, related parties.
 *
 * Rich rendering modes (in priority order):
 * 1. Note actions (`note_added` / `note_edited` / `note_deleted`) — render the
 *    note body as a green/red quote or word-level diff via `NoteActionDetail`.
 * 2. Enrichment metadata (`previousValues.enrichmentMeta`) — render the AI
 *    citations + model + fields touched via `EnrichmentMetaDetail`.
 * 3. Field-level before/after diffs — render a per-field row with old → new
 *    values via `FieldDiffDetail` whenever an entry carries both
 *    `previousValues` and `newValues` (the schema-widened shape introduced
 *    alongside the per-mutation `userId` foreign key).
 * 4. Legacy fallback — entries without `userId`/`newValues` keep rendering as
 *    a single reason line (existing behaviour).
 *
 * Practitioner identity:
 * Entries with a `userId` resolve through the `usersById` map (looked up by
 * the parent panel via `api.users.queries.listUsersByIds`) so the badge shows
 * the user's full name and an avatar instead of the JWT `name` claim. Old
 * entries (no `userId`) fall back to the literal `actor` string — there's no
 * way to recover the identity for those.
 */

import { useState } from "react";
import { diffWordsWithSpace } from "diff";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/composed/sections/collapsible";
import { Badge } from "@/ui/primitives/badge";
import {
  ArrowRight,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FileText,
  Globe,
  Sparkles,
} from "lucide-react";
import { UserAvatar } from "@/ui/composed/user-avatar";
import { cn } from "@/utils/misc";
import { usePersistedDisclosure } from "@/utils/use-persisted-disclosure";
import type { Id } from "@cvx/_generated/dataModel";
import { getActorInfo } from "./ChangeHistory.helpers";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChangeHistoryEntry {
  timestamp: number;
  actor: string;
  /** Stable user reference for human-practitioner entries written under the
   *  new schema. Absent on legacy entries and on agent/system-stamped rows. */
  userId?: Id<"users">;
  action: string;
  reason: string;
  previousValues?: Record<string, unknown>;
  /** Symmetric "after" payload for field-level diffs. Keys mirror
   *  `previousValues`. Absent on legacy entries. */
  newValues?: Record<string, unknown>;
}

/**
 * Display-ready user record returned by `api.users.queries.listUsersByIds`.
 * The parent panel resolves these once for the whole history feed and passes
 * a Map<userId, ResolvedUser> down so individual entries stay cheap.
 */
export interface ResolvedHistoryUser {
  _id: Id<"users">;
  firstName?: string;
  lastName?: string;
  email: string;
  avatarUrl?: string;
}

/** Per-field formatter map (e.g. currency for `amount`, label for enums). */
export type HistoryFieldFormatters = Record<string, (value: unknown) => string>;
/** Per-field display labels (e.g. `natureOfDebt` -> "Type of debt"). */
export type HistoryFieldLabels = Record<string, string>;

// ---------------------------------------------------------------------------
// Enrichment metadata helpers
// ---------------------------------------------------------------------------

interface EnrichmentCitationMeta {
  kind: "document" | "url";
  reference: string;
  label: string;
}

interface EnrichmentMeta {
  source?: "documents" | "web_search";
  citations?: Array<EnrichmentCitationMeta>;
  reasoning?: string;
  model?: string;
  agent?: string;
  fieldsTouched?: Array<string>;
  approvedBy?: string;
}

/**
 * Pulls structured enrichment metadata out of `previousValues.enrichmentMeta`
 * if present. Returns null for entries that aren't enrichment-tagged.
 */
function extractEnrichmentMeta(
  previousValues: Record<string, unknown> | undefined,
): EnrichmentMeta | null {
  if (!previousValues || typeof previousValues !== "object") return null;
  const meta = (previousValues as Record<string, unknown>).enrichmentMeta;
  if (!meta || typeof meta !== "object") return null;
  return meta as EnrichmentMeta;
}

// ---------------------------------------------------------------------------
// Note metadata (chronological notes feed entries)
// ---------------------------------------------------------------------------

interface NoteMeta {
  id?: string;
  /** Set on `note_added` and `note_deleted`. */
  content?: string;
  /** Set on `note_edited`. */
  contentBefore?: string;
  /** Set on `note_edited`. */
  contentAfter?: string;
}

/**
 * Pull the `previousValues.note` payload off a history entry. Returns
 * null when the entry isn't a note action.
 */
function extractNoteMeta(
  previousValues: Record<string, unknown> | undefined,
): NoteMeta | null {
  if (!previousValues || typeof previousValues !== "object") return null;
  const note = (previousValues as Record<string, unknown>).note;
  if (!note || typeof note !== "object") return null;
  return note as NoteMeta;
}

/**
 * Strip TipTap HTML down to plain text suitable for word-level diffing.
 * Block-level tags become single newlines; list items are prefixed
 * with `- `; everything else is dropped. Common entities are decoded
 * so the diff doesn't display `&amp;` instead of `&`.
 */
function htmlToPlainText(html: string): string {
  return html
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/p>\s*<p[^>]*>/gi, "\n\n")
    .replace(/<li[^>]*>/gi, "- ")
    .replace(/<\/li>/gi, "\n")
    .replace(/<\/(h[1-6]|div|p|ul|ol|blockquote)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// ---------------------------------------------------------------------------
// Date formatter
// ---------------------------------------------------------------------------

function formatShortDate(dateStr: string | undefined): string {
  if (!dateStr) return "\u2014";
  try {
    return new Date(dateStr).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

// ---------------------------------------------------------------------------
// Field-diff helpers
// ---------------------------------------------------------------------------

/**
 * Default pretty-printer for diff values when no per-agent formatter is
 * registered for the field. Strings pass through verbatim, booleans become
 * Yes/No, numbers are toLocaleString'd, null/undefined render as an em-dash.
 */
function defaultFormat(value: unknown): string {
  if (value === undefined || value === null) return "\u2014";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return value.toLocaleString("en-AU");
  if (typeof value === "string") return value.length > 0 ? value : "\u2014";
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * Returns the union of keys present on either side of a diff, preserving
 * the order from `newValues` first and appending any keys only on
 * `previousValues` afterwards.
 */
function diffFieldKeys(
  previous: Record<string, unknown> | undefined,
  next: Record<string, unknown> | undefined,
): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  const push = (k: string) => {
    if (!seen.has(k)) {
      seen.add(k);
      keys.push(k);
    }
  };
  Object.keys(next ?? {}).forEach(push);
  Object.keys(previous ?? {}).forEach(push);
  return keys;
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  // Cheap structural equality for plain JSON-ish payloads (sourceDocuments
  // arrays, simple objects). Good enough — we only use this to skip noise.
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ChangeHistoryProps {
  history: ChangeHistoryEntry[];
  /** Max entries to display (default 15) */
  maxEntries?: number;
  /**
   * Whether the section is expanded on first render. Defaults to
   * `false` (collapsed) so the existing summary-agent panels keep
   * their compact layout; the contacts panel opts-in to `true` so
   * the audit trail is visible without an extra click.
   */
  defaultOpen?: boolean;
  /** Title (default "History") */
  title?: string;
  /**
   * Map of userId -> resolved user info. Looked up by the parent panel via
   * `api.users.queries.listUsersByIds` and passed in so this component stays
   * a pure renderer (no Convex hooks). Optional — entries without a userId
   * fall back to the legacy free-text actor string.
   */
  usersById?: Map<Id<"users">, ResolvedHistoryUser>;
  /**
   * Per-field formatters for rendering before/after values in the diff
   * block (e.g. `amount: (v) => "$1,234.00"`). Falls back to a sensible
   * default per JS type when no formatter is registered.
   */
  formatters?: HistoryFieldFormatters;
  /**
   * Per-field display labels (e.g. `natureOfDebt: "Type of debt"`). Falls
   * back to the raw schema field name when no label is registered.
   */
  fieldLabels?: HistoryFieldLabels;
  /**
   * Stable id for remembering this history section's open/closed state per
   * user across sessions (localStorage). Omit to keep it ephemeral (the
   * original uncontrolled `defaultOpen` behaviour).
   */
  persistId?: string;
}

export function ChangeHistory({
  history,
  maxEntries = 15,
  title = "History",
  defaultOpen = false,
  usersById,
  formatters,
  fieldLabels,
  persistId,
}: ChangeHistoryProps) {
  const [open, setOpen] = usePersistedDisclosure(persistId, defaultOpen);

  if (history.length === 0) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="group flex w-full items-center justify-between">
        <div className="flex items-center gap-2">
          <h4 className="font-['Inter',-apple-system,BlinkMacSystemFont,sans-serif] text-xs font-medium uppercase tracking-widest text-[#121212]/70">
            {title}
          </h4>
          <Badge variant="secondary" size="sm">
            {history.length}
          </Badge>
        </div>
        <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-3">
        <div className="space-y-0">
          {history
            .slice()
            .reverse()
            .slice(0, maxEntries)
            .map((ch, i) => {
              const actorInfo = getActorInfo(ch, usersById);
              const enrichmentMeta = extractEnrichmentMeta(ch.previousValues);
              const noteMeta = extractNoteMeta(ch.previousValues);
              const isNoteAction =
                ch.action === "note_added" ||
                ch.action === "note_edited" ||
                ch.action === "note_deleted";
              const showFieldDiff =
                !isNoteAction &&
                !enrichmentMeta &&
                ch.previousValues != null &&
                ch.newValues != null;
              return (
                <div
                  key={i}
                  className="flex items-start gap-3 border-b border-border/50 py-2.5 last:border-0"
                >
                  {actorInfo.user ? (
                    <UserAvatar
                      avatarUrl={actorInfo.user.avatarUrl}
                      firstName={actorInfo.user.firstName}
                      lastName={actorInfo.user.lastName}
                      email={actorInfo.user.email}
                      className="mt-0.5 h-7 w-7 shrink-0"
                    />
                  ) : (
                    <div
                      className={cn(
                        "mt-0.5 shrink-0 rounded-md p-1.5",
                        actorInfo.bgColor,
                      )}
                    >
                      <actorInfo.Icon
                        className={cn("h-3.5 w-3.5", actorInfo.iconColor)}
                      />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <Badge
                          color={actorInfo.badgeColor}
                          variant="soft"
                          size="sm"
                          className="text-[10px]"
                        >
                          {actorInfo.label}
                        </Badge>
                        {actorInfo.viaMorgan && (
                          <span
                            className="inline-flex items-center gap-1 rounded-md border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 dark:text-amber-200"
                            title="Change applied via the Morgan chat agent on this user's behalf"
                          >
                            <Sparkles className="h-2.5 w-2.5" />
                            via Morgan
                          </span>
                        )}
                      </div>
                      <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
                        {formatShortDate(new Date(ch.timestamp).toISOString())}
                      </span>
                    </div>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                      {isNoteAction && ch.actor
                        ? `${actorInfo.label} \u00B7 ${ch.reason}`
                        : ch.reason}
                    </p>
                    {isNoteAction && noteMeta && (
                      <NoteActionDetail action={ch.action} meta={noteMeta} />
                    )}
                    {enrichmentMeta && (
                      <EnrichmentMetaDetail meta={enrichmentMeta} />
                    )}
                    {showFieldDiff && (
                      <FieldDiffDetail
                        previousValues={ch.previousValues}
                        newValues={ch.newValues}
                        formatters={formatters}
                        fieldLabels={fieldLabels}
                      />
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ---------------------------------------------------------------------------
// Field-level before / after diff (the new rich rendering)
// ---------------------------------------------------------------------------

/**
 * Renders one row per changed field with a `Label  Before -> After` layout.
 *
 * - Skips fields whose value is identical on both sides (noise from the
 *   agent-side mutation that copies the full updates payload).
 * - Skips internal-only fields (`enrichmentMeta`, `note`) that have their
 *   own dedicated detail blocks rendered above.
 * - Long-form string changes (anything without a registered formatter) get
 *   a word-level diff via `diffWordsWithSpace`.
 * - Short scalar changes render as `before -> after` with a horizontal
 *   arrow icon between them.
 */
function FieldDiffDetail({
  previousValues,
  newValues,
  formatters,
  fieldLabels,
}: {
  previousValues: Record<string, unknown> | undefined;
  newValues: Record<string, unknown> | undefined;
  formatters?: HistoryFieldFormatters;
  fieldLabels?: HistoryFieldLabels;
}) {
  const SKIP_KEYS = new Set(["enrichmentMeta", "note"]);
  const keys = diffFieldKeys(previousValues, newValues).filter(
    (k) => !SKIP_KEYS.has(k),
  );
  const rows = keys
    .map((key) => {
      const before = previousValues?.[key];
      const after = newValues?.[key];
      if (valuesEqual(before, after)) return null;
      return { key, before, after };
    })
    .filter(
      (r): r is { key: string; before: unknown; after: unknown } => r !== null,
    );

  if (rows.length === 0) return null;

  return (
    <div className="mt-2 flex min-w-0 flex-col gap-1.5 overflow-hidden rounded-md border border-border/60 bg-muted/30 p-2 text-[11px]">
      {rows.map(({ key, before, after }) => (
        <FieldDiffRow
          key={key}
          field={key}
          before={before}
          after={after}
          formatter={formatters?.[key]}
          label={fieldLabels?.[key] ?? key}
        />
      ))}
    </div>
  );
}

function FieldDiffRow({
  field,
  before,
  after,
  formatter,
  label,
}: {
  field: string;
  before: unknown;
  after: unknown;
  formatter?: (value: unknown) => string;
  label: string;
}) {
  // Long-form text fields (notes, descriptions, addresses) — render an
  // inline word-level diff so the practitioner can see exactly what
  // changed without needing to read both versions in full. We trigger
  // this only when no per-agent formatter is registered AND both sides
  // are non-empty strings of meaningful length.
  const isLongString =
    !formatter &&
    typeof before === "string" &&
    typeof after === "string" &&
    (before.length > 60 || after.length > 60);

  const renderedBefore = formatter ? formatter(before) : defaultFormat(before);
  const renderedAfter = formatter ? formatter(after) : defaultFormat(after);

  if (isLongString) {
    const parts = diffWordsWithSpace(before as string, after as string);
    return (
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <p className="whitespace-pre-wrap break-words leading-relaxed text-foreground [overflow-wrap:anywhere]">
          {parts.map((part, i) => {
            if (part.added) {
              return (
                <span
                  key={i}
                  className="rounded-sm bg-emerald-100 px-0.5 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100"
                >
                  {part.value}
                </span>
              );
            }
            if (part.removed) {
              return (
                <span
                  key={i}
                  className="rounded-sm bg-rose-100 px-0.5 text-rose-900 line-through dark:bg-rose-900/40 dark:text-rose-100"
                >
                  {part.value}
                </span>
              );
            }
            return <span key={i}>{part.value}</span>;
          })}
        </p>
      </div>
    );
  }

  return (
    <div className="grid min-w-0 grid-cols-[7rem_1fr] items-baseline gap-2">
      <span className="truncate text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-foreground">
        <span
          className={cn(
            "break-words rounded-sm bg-rose-50 px-1 py-0.5 tabular-nums text-rose-900 line-through [overflow-wrap:anywhere] dark:bg-rose-950/40 dark:text-rose-200",
            field === "amount" && "font-mono",
          )}
        >
          {renderedBefore}
        </span>
        <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />
        <span
          className={cn(
            "break-words rounded-sm bg-emerald-50 px-1 py-0.5 tabular-nums text-emerald-900 [overflow-wrap:anywhere] dark:bg-emerald-950/40 dark:text-emerald-200",
            field === "amount" && "font-mono",
          )}
        >
          {renderedAfter}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Enrichment metadata detail row
// ---------------------------------------------------------------------------

function EnrichmentMetaDetail({ meta }: { meta: EnrichmentMeta }) {
  const citations = meta.citations ?? [];
  if (citations.length === 0 && !meta.model) return null;
  return (
    <div className="mt-2 flex flex-col gap-1.5 rounded-md border border-amber-200/60 bg-amber-50/50 p-2 text-[11px] text-foreground dark:border-amber-900/40 dark:bg-amber-950/20">
      {meta.model && (
        <p className="text-muted-foreground">
          <span className="font-medium text-foreground">Updated by AI</span>
          {meta.fieldsTouched && meta.fieldsTouched.length > 0 ? (
            <> \u00B7 {meta.fieldsTouched.join(", ")}</>
          ) : null}
        </p>
      )}
      {citations.length > 0 && (
        <ul className="flex flex-col gap-1">
          {citations.slice(0, 5).map((c, i) => (
            <li key={`cite-${i}`} className="flex min-w-0 items-center gap-1.5">
              {c.kind === "document" ? (
                <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
              ) : (
                <Globe className="h-3 w-3 shrink-0 text-muted-foreground" />
              )}
              {c.kind === "url" ? (
                <a
                  href={c.reference}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 truncate text-primary hover:underline"
                  title={c.reference}
                >
                  <span className="truncate">{c.label}</span>
                  <ExternalLink className="h-3 w-3 shrink-0" />
                </a>
              ) : (
                <span
                  className="truncate text-muted-foreground"
                  title={c.label}
                >
                  {c.label}
                </span>
              )}
            </li>
          ))}
          {citations.length > 5 && (
            <li className="text-[10px] text-muted-foreground">
              +{citations.length - 5} more
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Note-action detail (chronological notes feed entries)
// ---------------------------------------------------------------------------

/**
 * Renders the right-hand body of a `note_added` / `note_edited` /
 * `note_deleted` history entry.
 *
 * - `note_added`: the new note's content is shown as a quote.
 * - `note_edited`: a word-level diff (jsdiff) between the before and
 *   after content with an expandable raw before/after view.
 * - `note_deleted`: the original content struck through.
 */
function NoteActionDetail({
  action,
  meta,
}: {
  action: string;
  meta: NoteMeta;
}) {
  if (action === "note_added") {
    const text = htmlToPlainText(meta.content ?? "");
    if (!text) return null;
    return <NoteQuote text={text} tone="added" />;
  }
  if (action === "note_deleted") {
    const text = htmlToPlainText(meta.content ?? "");
    if (!text) return null;
    return <NoteQuote text={text} tone="deleted" />;
  }
  if (action === "note_edited") {
    const before = htmlToPlainText(meta.contentBefore ?? "");
    const after = htmlToPlainText(meta.contentAfter ?? "");
    if (!before && !after) return null;
    return <NoteEditedDiff before={before} after={after} />;
  }
  return null;
}

function NoteQuote({
  text,
  tone,
}: {
  text: string;
  tone: "added" | "deleted";
}) {
  // Wrap the whole body in a single inline-style highlight (added →
  // green, deleted → red strike-through). No surrounding card or
  // side-bar — keeps the history feed light and lets the text colour
  // itself carry the meaning.
  return (
    <p
      className={cn(
        "mt-1.5 whitespace-pre-wrap break-words text-[12px] leading-relaxed [overflow-wrap:anywhere]",
        tone === "added"
          ? "text-emerald-900 dark:text-emerald-100"
          : "text-rose-900/80 line-through dark:text-rose-200/80",
      )}
    >
      <span
        className={cn(
          "rounded-sm px-1 py-0.5",
          tone === "added"
            ? "bg-emerald-100 dark:bg-emerald-900/40"
            : "bg-rose-100 dark:bg-rose-900/40",
        )}
      >
        {text}
      </span>
    </p>
  );
}

function NoteEditedDiff({ before, after }: { before: string; after: string }) {
  const [showRaw, setShowRaw] = useState(false);
  // jsdiff emits one part per word/whitespace run, tagged added/removed/unchanged.
  const parts = diffWordsWithSpace(before, after);

  const hasChanges = parts.some((p) => p.added || p.removed);
  if (!hasChanges) return null;

  return (
    <div className="mt-1.5 flex min-w-0 flex-col gap-1 text-[12px] leading-relaxed">
      <p className="whitespace-pre-wrap break-words text-foreground [overflow-wrap:anywhere]">
        {parts.map((part, i) => {
          if (part.added) {
            return (
              <span
                key={i}
                className="rounded-sm bg-emerald-100 px-0.5 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100"
              >
                {part.value}
              </span>
            );
          }
          if (part.removed) {
            return (
              <span
                key={i}
                className="rounded-sm bg-rose-100 px-0.5 text-rose-900 line-through dark:bg-rose-900/40 dark:text-rose-100"
              >
                {part.value}
              </span>
            );
          }
          return <span key={i}>{part.value}</span>;
        })}
      </p>
      <button
        type="button"
        onClick={() => setShowRaw((s) => !s)}
        className="inline-flex items-center gap-1 self-start text-[10px] font-medium text-muted-foreground hover:text-foreground"
      >
        {showRaw ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        {showRaw ? "Hide raw before/after" : "View raw before/after"}
      </button>
      {showRaw && (
        <div className="mt-1 grid min-w-0 grid-cols-1 gap-2 text-[11px] sm:grid-cols-2">
          <div className="min-w-0">
            <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Before
            </p>
            <p className="whitespace-pre-wrap break-words text-foreground/80 [overflow-wrap:anywhere]">
              {before || "(empty)"}
            </p>
          </div>
          <div className="min-w-0">
            <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              After
            </p>
            <p className="whitespace-pre-wrap break-words text-foreground/80 [overflow-wrap:anywhere]">
              {after || "(empty)"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
